import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Home, Users, Briefcase, Book, Calendar, Bell, Image, MessageSquare, Mail, UploadCloud, Activity, BarChart2, ChevronRight, Plus, Trash2, CheckCircle, Shield
} from 'lucide-react';
import AdminSidebar from './components/AdminSidebar';
import AdminHeader from './components/AdminHeader';
import StatCard from './components/StatCard';
import UsersSection from './components/UsersSection';
import JobsSection from './components/JobsSection';
import TutorialsSection from './components/TutorialsSection';
import EventsSection from './components/EventsSection';
import BroadcastSection from './components/BroadcastSection';
import MessagesSection from './components/MessagesSection';
import AdsSection from './components/AdsSection';
import NewsletterSection from './components/NewsletterSection';
import ContributionsSection from './components/ContributionsSection';
import ExplorerSection from './components/ExplorerSection';
import AnalyticsSection from './components/AnalyticsSection';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  db, auth, collection, getDocs, doc, updateDoc, deleteDoc,
  query, orderBy, addDoc, serverTimestamp, getDoc, where, onSnapshot, limit, startAfter
} from '../../components/firebase';
import JobDetailModal from '../Company/components/JobDetailModal';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Recharts and ReactQuill are now handled within modular components where needed.

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'snaped4338@gmail.com';

// StatCard now moved to components/StatCard.js

const AdminPanel = () => {
  const { isAdmin, user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ users: 0, tutorials: 0, contacts: 0, notifications: 0, events: 0 });
  const [events, setEvents] = useState([]);
  const [participants, setParticipants] = useState({}); // { eventId: [participantData] }
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [selectedEventForParticipants, setSelectedEventForParticipants] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [contribLoading, setContribLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTutorial, setNewTutorial] = useState({ title: '', description: '', category: 'General', topics: [{ title: '', content: '' }] });
  const [newNotification, setNewNotification] = useState({ title: '', message: '', type: 'info' });
  const [notificationRecipient, setNotificationRecipient] = useState('all');
  const [selectedRecipientUid, setSelectedRecipientUid] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [ads, setAds] = useState([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [newAd, setNewAd] = useState({ title: '', imageUrl: '', targetUrl: '', type: 'banner', isActive: true, width: '', height: '' });
  const [subscribers, setSubscribers] = useState([]);
  const [adminJobs, setAdminJobs] = useState([]);
  const [adminJobFilter, setAdminJobFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [jobToReject, setJobToReject] = useState(null);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [explorerCollection, setExplorerCollection] = useState('Users');
  const [explorerData, setExplorerData] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerSearch, setExplorerSearch] = useState('');
  const [explorerLastDoc, setExplorerLastDoc] = useState(null);
  const [explorerFirstDoc, setExplorerFirstDoc] = useState(null);
  const [explorerPageIndex, setExplorerPageIndex] = useState(0);
  const [explorerPageSize] = useState(10);
  const [explorerHasMore, setExplorerHasMore] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', time: '', location: '', description: '', type: 'online' });

  const colors = {
    bg: 'var(--bg-main)',
    sidebar: 'var(--bg-sidebar)',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
    danger: '#ef4444',
    success: '#22c55e'
  };

  // Redirect if not admin
  useEffect(() => {
    if (user && user.email !== ADMIN_EMAIL) {
      navigate('/home');
    }
  }, [user, navigate]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users
      const usersSnap = await getDocs(collection(db, 'Users'));
      const usersData = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(usersData);

      // Fetch tutorials/posts
      const tutSnap = await getDocs(collection(db, 'posts'));
      const tutData = tutSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTutorials(tutData);

      // Fetch contact messages (try contactResponses and sheetdb)
      try {
        const contactSnap = await getDocs(collection(db, 'contactResponses'));
        setContacts(contactSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { setContacts([]); }

      try {
        const notifSnap = await getDocs(query(collection(db, 'notifications'), orderBy('createdAt', 'desc')));
        // We'll update the query to 'timestamp' in the next step, but for now, let's at least sort it.
        // Actually, let's just use the query for 'timestamp' later after we fix the send logic.
        setNotifications(notifSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        // Fallback for when 'timestamp' index isn't ready or field is missing
        const notifSnap = await getDocs(collection(db, 'notifications'));
        setNotifications(notifSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0)));
      }

      // Fetch Ads
      try {
        const adSnap = await getDocs(collection(db, 'ads'));
        setAds(adSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { setAds([]); }

      // Fetch newsletter subscribers
      try {
        const newsletterSnap = await getDocs(collection(db, 'newsletter'));
        const sorted = newsletterSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const dateA = a.timestamp instanceof Date ? a.timestamp : (a.timestamp?.toDate ? a.timestamp.toDate() : new Date(0));
            const dateB = b.timestamp instanceof Date ? b.timestamp : (b.timestamp?.toDate ? b.timestamp.toDate() : new Date(0));
            return dateB - dateA;
          });
        setSubscribers(sorted);
      } catch { setSubscribers([]); }

      // Fetch Events
      let evCount = 0;
      try {
        const eventSnap = await getDocs(query(collection(db, 'events'), orderBy('createdAt', 'desc')));
        const eventData = eventSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setEvents(eventData);
        evCount = eventData.length;
      } catch (err) { console.error("Error fetching events:", err); setEvents([]); }

      // Fetch Contributions
      let contribCount = 0;
      try {
        const contribSnap = await getDocs(query(collection(db, 'contributions'), orderBy('timestamp', 'desc')));
        const cData = contribSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setContributions(cData);
        contribCount = cData.length;
      } catch (err) { console.error("Error fetching contributions:", err); setContributions([]); }

      // Real-time jobs are handled by a separate useEffect with onSnapshot

      setStats({
        users: usersSnap.docs.length,
        tutorials: tutSnap.docs.length,
        contacts: contacts.length,
        notifications: notifications.length,
        events: evCount,
        contributions: contribCount,
        pendingJobs: adminJobs.filter(j => j.status === 'pending').length
      });
    } catch (err) {
      console.error(err);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  }, [contacts.length, notifications.length]);

  const fetchExplorerData = async (direction = 'initial') => {
    setExplorerLoading(true);
    try {
      const baseRef = collection(db, explorerCollection);
      let q;

      if (direction === 'next' && explorerLastDoc) {
        q = query(baseRef, limit(explorerPageSize + 1), startAfter(explorerLastDoc));
      } else {
        // 'initial' or 'prev' both reset to first page for simplicity
        q = query(baseRef, limit(explorerPageSize + 1));
        if (direction !== 'next') setExplorerPageIndex(0);
      }

      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (docs.length > explorerPageSize) {
        setExplorerHasMore(true);
        setExplorerData(docs.slice(0, explorerPageSize));
        setExplorerLastDoc(snap.docs[explorerPageSize - 1]);
      } else {
        setExplorerHasMore(false);
        setExplorerData(docs);
        setExplorerLastDoc(snap.docs[snap.docs.length - 1] || null);
      }
      setExplorerFirstDoc(snap.docs[0] || null);

      if (direction === 'next') setExplorerPageIndex(prev => prev + 1);
      else if (direction === 'prev') setExplorerPageIndex(prev => Math.max(0, prev - 1));

    } catch (err) {
      console.error(err);
      toast.error(`Error fetching "${explorerCollection}". Check the collection name.`);
      setExplorerData([]);
    } finally {
      setExplorerLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeSection === 'explorer') {
      setExplorerLastDoc(null);
      setExplorerFirstDoc(null);
      setExplorerPageIndex(0);
      setExplorerData([]);
      // Fetch fresh data for the newly selected collection
      const baseRef = collection(db, explorerCollection);
      const q = query(baseRef, limit(explorerPageSize + 1));
      setExplorerLoading(true);
      getDocs(q)
        .then(snap => {
          const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          if (docs.length > explorerPageSize) {
            setExplorerHasMore(true);
            setExplorerData(docs.slice(0, explorerPageSize));
            setExplorerLastDoc(snap.docs[explorerPageSize - 1]);
          } else {
            setExplorerHasMore(false);
            setExplorerData(docs);
            setExplorerLastDoc(snap.docs[snap.docs.length - 1] || null);
          }
          setExplorerFirstDoc(snap.docs[0] || null);
        })
        .catch(err => {
          console.error(err);
          toast.error(`Could not load "${explorerCollection}"`);
          setExplorerData([]);
        })
        .finally(() => setExplorerLoading(false));
    }
    // Only re-run when the collection or active section changes — NOT when fetch function changes
  }, [explorerCollection, activeSection]);



  const formatDate = (timestamp) => {
    if (!timestamp) return 'No Date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Real-time listener for jobs
  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) return;

    const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const jobsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setAdminJobs(jobsData);
      setStats(prev => ({
        ...prev,
        pendingJobs: jobsData.filter(j => j.status === 'pending').length,
        totalJobs: jobsData.length
      }));
    }, (err) => {
      console.error("Error listening to jobs:", err);
      toast.error('Real-time connection failed');
    });

    return () => unsubscribe();
  }, [user]);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'Users', userId));
        setUsers(prev => prev.filter(u => u.id !== userId));
        toast.success('User deleted from database');
      } catch { toast.error('Failed to delete user'); }
    }
  };

  const handleViewParticipants = async (ev) => {
    try {
      const pSnap = await getDocs(query(collection(db, 'event_participants'), where('eventId', '==', ev.id)));
      const pData = pSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSelectedEventForParticipants(ev);
      setParticipants(prev => ({ ...prev, [ev.id]: pData }));
      setShowParticipantModal(true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load participants');
    }
  };

  const handleDeleteRegistration = async (p) => {
    if (window.confirm('Delete this registration?')) {
      try {
        await deleteDoc(doc(db, 'event_participants', p.id));
        setParticipants(prev => ({
          ...prev,
          [selectedEventForParticipants.id]: prev[selectedEventForParticipants.id].filter(x => x.id !== p.id)
        }));
        toast.success('Registration removed');
      } catch (err) {
        console.error(err);
        toast.error('Failed to remove registration');
      }
    }
  };

  const syncUsers = async () => {
    toast.loading('Checking for missing user records...', { id: 'sync' });
    try {
      // Note: Full auth sync requires a Cloud Function on Blaze plan.
      // This is a placeholder for the user to understand why counts differ.
      toast.success('Auth-Firestore sync active. Users will sync on their next login.', { id: 'sync' });
    } catch { toast.error('Sync failed', { id: 'sync' }); }
  };

  const addEvent = async () => {
    try {
      if (!newEvent.title || !newEvent.date) {
        toast.warning('Please provide at least a title and date');
        return;
      }
      await addDoc(collection(db, 'events'), {
        ...newEvent,
        createdAt: serverTimestamp(),
        timestamp: serverTimestamp()
      });
      toast.success('Event added successfully');
      setShowEventModal(false);
      setNewEvent({ title: '', date: '', time: '', location: '', description: '', type: 'online' });
      fetchAllData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add event');
    }
  };

  const deleteEvent = async (id) => {
    if (window.confirm('Delete this event?')) {
      try {
        await deleteDoc(doc(db, 'events', id));
        toast.success('Event deleted');
        fetchAllData();
      } catch (err) {
        console.error(err);
        toast.error('Failed to delete event');
      }
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'Users', userId), { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch { toast.error('Failed to update role'); }
  };

  const deleteTutorial = async (tutId) => {
    if (!window.confirm('Delete this tutorial?')) return;
    try {
      await deleteDoc(doc(db, 'posts', tutId));
      setTutorials(prev => prev.filter(t => t.id !== tutId));
      toast.success('Tutorial deleted');
    } catch { toast.error('Failed to delete tutorial'); }
  };

  const addTutorial = async () => {
    if (!newTutorial.title || !newTutorial.description) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const docRef = await addDoc(collection(db, 'posts'), {
        ...newTutorial,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        type: newTutorial.isRoadmap ? 'roadmap' : 'tutorial'
      });
      setTutorials(prev => [...prev, { id: docRef.id, ...newTutorial }]);
      setNewTutorial({ title: '', description: '', category: 'General', topics: [{ title: '', content: '' }], isRoadmap: false });
      setShowAddModal(false);
      toast.success('Tutorial added!');
    } catch { toast.error('Failed to add tutorial'); }
  };

  const handleJsonImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          // Assuming roadmap.sh format or similar hierarchical structure
          setNewTutorial({
            title: jsonData.title || 'Imported Roadmap',
            description: jsonData.description || 'Roadmap imported from JSON',
            category: 'Roadmaps',
            isRoadmap: true,
            topics: jsonData.topics || [{ title: 'Overview', content: event.target.result }],
            roadmapData: jsonData // Save full data for custom rendering
          });
          toast.success('JSON Roadmap imported! Review and save.');
        } catch (err) {
          toast.error('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  const addTopic = () => {
    setNewTutorial(p => ({
      ...p,
      topics: [...p.topics, { title: '', content: '' }]
    }));
  };

  const removeTopic = (index) => {
    setNewTutorial(p => ({
      ...p,
      topics: p.topics.filter((_, i) => i !== index)
    }));
  };

  const updateTopic = (index, field, value) => {
    setNewTutorial(p => ({
      ...p,
      topics: p.topics.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image', 'code-block'],
      ['clean']
    ],
  };

  const sendNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error('Please fill all notification fields');
      return;
    }

    if (notificationRecipient === 'specific' && !selectedRecipientUid) {
      toast.error('Please select a recipient user');
      return;
    }

    try {
      await addDoc(collection(db, 'notifications'), {
        ...newNotification,
        recipient: notificationRecipient,
        recipientUid: notificationRecipient === 'specific' ? selectedRecipientUid : null,
        timestamp: serverTimestamp(), // Use timestamp to match Notification.jsx
        createdAt: serverTimestamp(), // Keep createdAt for legacy/history if needed
        createdBy: user.uid,
        readBy: []
      });
      toast.success(notificationRecipient === 'all' ? 'Notification sent to all users!' : 'Targeted notification sent!');
      setNewNotification({ title: '', message: '', type: 'info' });
      setSelectedRecipientUid('');
      fetchAllData();
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error('Failed to send notification');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB limit for Base64 (Firestore has 1MB doc limit)
        toast.error('Image too large. Please use an image under 1MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewAd(p => ({ ...p, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addAd = async () => {
    if (!newAd.imageUrl || !newAd.targetUrl) {
      toast.error('Please fill image and target URLs');
      return;
    }
    try {
      const docRef = await addDoc(collection(db, 'ads'), {
        ...newAd,
        createdAt: serverTimestamp()
      });
      setAds(prev => [...prev, { id: docRef.id, ...newAd }]);
      setNewAd({ title: '', imageUrl: '', targetUrl: '', type: 'banner', isActive: true, width: '', height: '' });
      setShowAdModal(false);
      toast.success('Ad added successfully!');
    } catch { toast.error('Failed to add ad'); }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await deleteDoc(doc(db, 'ads', adId));
      setAds(prev => prev.filter(a => a.id !== adId));
      toast.success('Ad deleted');
    } catch { toast.error('Failed to delete ad'); }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to permanently delete this job posting? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'jobs', jobId));
      toast.success('Job deleted successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete job');
    }
  };

  const toggleAdStatus = async (ad) => {
    try {
      await updateDoc(doc(db, 'ads', ad.id), { isActive: !ad.isActive });
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, isActive: !ad.isActive } : a));
      toast.success(`Ad ${!ad.isActive ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Failed to update ad status'); }
  };

  const handleContributionStatus = async (contrib, newStatus) => {
    try {
      await updateDoc(doc(db, 'contributions', contrib.id), { status: newStatus });
      setContributions(prev => prev.map(c => c.id === contrib.id ? { ...c, status: newStatus } : c));

      // If approved, give points to user
      if (newStatus === 'Approved') {
        const userRef = doc(db, 'Users', contrib.userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const currentPoints = userSnap.data().points || 0;
          await updateDoc(userRef, { points: currentPoints + 50 }); // Reward 50 points per approved contribution
          toast.success(`Contribution approved! 50 points rewarded to ${contrib.userEmail}`);
        }
      } else {
        toast.info(`Contribution marked as ${newStatus}`);
      }
    } catch { toast.error('Failed to update contribution status'); }
  };

  const handleJobApproval = async (job, newStatus, reason = '') => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'rejected' && reason) {
        updateData.rejectionReason = reason;
      }

      await updateDoc(doc(db, 'jobs', job.id), updateData);
      setAdminJobs(prev => prev.map(j => j.id === job.id ? { ...j, ...updateData } : j));

      // Notify the company
      const statusLabel = newStatus === 'approved' ? 'approved and is now live' : 'not approved';
      await addDoc(collection(db, 'notifications'), {
        title: newStatus === 'approved' ? 'Job Approved! 🎉' : 'Job Not Approved',
        message: newStatus === 'approved'
          ? `Your job posting "${job.title}" has been approved and is now live.`
          : `Your job posting "${job.title}" was not approved. Reason: ${reason || 'No specific reason provided.'}`,
        type: newStatus === 'approved' ? 'success' : 'warning',
        recipient: 'specific',
        recipientUid: job.companyId,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        readBy: []
      });

      toast.success(`Job ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
      if (newStatus === 'rejected') {
        setShowRejectionModal(false);
        setRejectionReason('');
        setJobToReject(null);
      }
    } catch (err) {
      toast.error('Failed to update job status');
    }
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <Home size={18} /> },
    { id: 'users', label: 'Users', icon: <Users size={18} />, badge: stats.users },
    { id: 'jobs', label: 'Job Management', icon: <Briefcase size={18} />, badge: adminJobs.length },
    { id: 'tutorials', label: 'CodeCampus', icon: <Book size={18} />, badge: stats.tutorials },
    { id: 'events', label: 'Events Hub', icon: <Calendar size={18} />, badge: events.length },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { id: 'ads', label: 'Manage Ads', icon: <Image size={18} />, badge: ads.length },
    { id: 'contacts', label: 'Messages', icon: <MessageSquare size={18} />, badge: contacts.length },
    { id: 'newsletter', label: 'Newsletter', icon: <Mail size={18} />, badge: subscribers.length },
    { id: 'contributions', label: 'Contributions', icon: <UploadCloud size={18} />, badge: contributions.filter(c => c.status === 'Pending').length },
    { id: 'db_explorer', label: 'DB Explorer', icon: <Activity size={18} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart2 size={18} /> },
  ];

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTutorials = tutorials.filter(t =>
    t.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    background: colors.card, border: `1px solid ${colors.border}`,
    color: colors.text, fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit'
  };

  if (!isAdmin && user) return (
    <div className="min-h-screen flex items-center justify-center flex-col gap-4">
      <Shield size={64} className="text-destructive mb-2" />
      <h2 className="text-3xl font-black text-foreground">Access Denied</h2>
      <p className="text-muted-foreground font-bold">You don't have admin privileges.</p>
      <Button onClick={() => navigate('/home')} className="rounded-2xl px-8">Go Home</Button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, display: 'flex', position: 'relative', overflow: 'hidden' }}>
      <ToastContainer theme={isDarkMode ? 'dark' : 'light'} position="top-right" />

      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              zIndex: 99998, backdropFilter: 'blur(2px)'
            }}
          />
        )}
      </AnimatePresence>

      <AdminSidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isMobile={isMobile}
        stats={stats}
        adminJobs={adminJobs}
        events={events}
        ads={ads}
        contacts={contacts}
        subscribers={subscribers}
        contributions={contributions}
      />

      {/* Main Content */}
      <div style={{
        flex: 1,
        padding: isMobile ? '16px' : '24px 28px',
        overflowY: 'auto',
        maxHeight: '100vh',
        minWidth: 0,
        marginLeft: (!isMobile && !sidebarOpen) ? 0 : undefined
      }}>
        <AdminHeader
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeSection={activeSection}
          sidebarItems={sidebarItems}
          user={user}
          fetchAllData={fetchAllData}
          isMobile={isMobile}
        />

        {/* DASHBOARD */}
        {activeSection === 'dashboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8 mt-2">
              <StatCard icon={<Users size={20} />} label="Total Users" value={stats.users} color="#8b5cf6" bg="#8b5cf620" />
              <StatCard icon={<Book size={20} />} label="Tutorials" value={tutorials.length} color="#06b6d4" bg="#06b6d420" />
              <StatCard icon={<Calendar size={20} />} label="Events" value={events.length} color="#ec4899" bg="#ec489920" />
              <StatCard icon={<MessageSquare size={20} />} label="Messages" value={contacts.length} color="#f59e0b" bg="#f59e0b20" />
              <StatCard icon={<Bell size={20} />} label="Notifications" value={notifications.length} color="#22c55e" bg="#22c55e20" />
            </div>

            {/* Recent Users */}
            <div className="bg-card/40 backdrop-blur-xl border border-border/40 rounded-[2rem] p-6 md:p-8 mb-8 shadow-xl">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-foreground tracking-tight">Recent Users</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveSection('users')}
                  className="rounded-xl border-border/60 hover:bg-primary/10 hover:text-primary font-bold gap-2"
                >
                  View All <ChevronRight size={14} />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {users.slice(0, 5).map((u, i) => (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-[1.25rem] bg-background/40 border border-border/40 hover:border-primary/20 hover:bg-background/60 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-black text-lg shadow-sm group-hover:scale-105 transition-transform">
                      {(u.displayName || u.email || 'U')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground font-black text-sm md:text-base tracking-tight truncate">
                        {u.displayName || 'Anonymous User'}
                      </div>
                      <div className="text-muted-foreground text-xs md:text-sm font-bold truncate opacity-70">
                        {u.email}
                      </div>
                    </div>
                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${u.role === 'admin'
                      ? 'bg-rose-500/10 border-rose-500/20 text-rose-500'
                      : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                      }`}>
                      {u.role || 'user'}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeSection === 'users' && (
          <UsersSection
            users={users}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            syncUsers={syncUsers}
            updateUserRole={updateUserRole}
            deleteUser={deleteUser}
            isMobile={isMobile}
          />
        )}

        {activeSection === 'tutorials' && (
          <TutorialsSection
            filteredTutorials={filteredTutorials}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            newTutorial={newTutorial}
            setNewTutorial={setNewTutorial}
            addTutorial={addTutorial}
            deleteTutorial={deleteTutorial}
            handleJsonImport={handleJsonImport}
            addTopic={addTopic}
            updateTopic={updateTopic}
            removeTopic={removeTopic}
            isMobile={isMobile}
          />
        )}

        {/* EVENTS HUB */}
        {activeSection === 'events' && (
          <EventsSection
            events={events}
            showEventModal={showEventModal}
            setShowEventModal={setShowEventModal}
            newEvent={newEvent}
            setNewEvent={setNewEvent}
            addEvent={addEvent}
            deleteEvent={deleteEvent}
            participants={participants}
            handleViewParticipants={handleViewParticipants}
            selectedEventForParticipants={selectedEventForParticipants}
            showParticipantModal={showParticipantModal}
            setShowParticipantModal={setShowParticipantModal}
            handleDeleteRegistration={handleDeleteRegistration}
            isMobile={isMobile}
          />
        )}

        {/* NOTIFICATIONS */}
        {activeSection === 'notifications' && (
          <BroadcastSection
            newNotification={newNotification}
            setNewNotification={setNewNotification}
            sendNotification={sendNotification}
            notifications={notifications}
            setNotifications={setNotifications}
            notificationRecipient={notificationRecipient}
            setNotificationRecipient={setNotificationRecipient}
            userSearchQuery={userSearchQuery}
            setUserSearchQuery={setUserSearchQuery}
            selectedRecipientUid={selectedRecipientUid}
            setSelectedRecipientUid={setSelectedRecipientUid}
            users={users}
            deleteDoc={deleteDoc}
            doc={doc}
            db={db}
            toast={toast}
            isMobile={isMobile}
          />
        )}

        {/* CONTACT MESSAGES */}
        {activeSection === 'contacts' && (
          <MessagesSection
            contacts={contacts}
            deleteDoc={deleteDoc}
            doc={doc}
            db={db}
            setContacts={setContacts}
            toast={toast}
            formatDate={formatDate}
            newNotification={newNotification}
            setNewNotification={setNewNotification}
            setNotificationRecipient={setNotificationRecipient}
            sendNotification={sendNotification}
            isMobile={isMobile}
          />
        )}

        {/* MANAGE ADS */}
        {activeSection === 'ads' && (
          <AdsSection
            ads={ads}
            showAdModal={showAdModal}
            setShowAdModal={setShowAdModal}
            newAd={newAd}
            setNewAd={setNewAd}
            addAd={addAd}
            deleteAd={deleteAd}
            toggleAdStatus={toggleAdStatus}
            isMobile={isMobile}
          />
        )}

        {/* NEWSLETTER SUBSCRIBERS */}
        {activeSection === 'newsletter' && (
          <NewsletterSection
            subscribers={subscribers}
            isMobile={isMobile}
          />
        )}

        {/* CONTRIBUTIONS MANAGEMENT */}
        {activeSection === 'contributions' && (
          <ContributionsSection
            contributions={contributions}
            handleContributionStatus={handleContributionStatus}
            deleteDoc={deleteDoc}
            doc={doc}
            db={db}
            setContributions={setContributions}
            toast={toast}
            formatDate={formatDate}
          />
        )}

        {activeSection === 'jobs' && (
          <JobsSection
            adminJobs={adminJobs}
            adminJobFilter={adminJobFilter}
            setAdminJobFilter={setAdminJobFilter}
            searchQuery={searchQuery}
            setSelectedJobForDetails={setSelectedJobForDetails}
            handleJobApproval={handleJobApproval}
            setJobToReject={setJobToReject}
            setShowRejectionModal={setShowRejectionModal}
            deleteJob={deleteJob}
            isMobile={isMobile}
          />
        )}

        {/* DB EXPLORER */}
        {activeSection === 'explorer' && (
          <ExplorerSection
            explorerCollection={explorerCollection}
            setExplorerCollection={setExplorerCollection}
            explorerSearch={explorerSearch}
            setExplorerSearch={setExplorerSearch}
            explorerLoading={explorerLoading}
            explorerData={explorerData}
            fetchExplorerData={fetchExplorerData}
            pageIndex={explorerPageIndex}
            hasMore={explorerHasMore}
          />
        )}

        {/* ANALYTICS */}
        {activeSection === 'analytics' && (
          <AnalyticsSection
            users={users}
            subscribers={subscribers}
            tutorials={tutorials}
            contacts={contacts}
            notifications={notifications}
            isMobile={isMobile}
          />
        )}
      </div>

      <JobDetailModal
        show={!!selectedJobForDetails}
        onClose={() => setSelectedJobForDetails(null)}
        job={selectedJobForDetails}
      />

      <AnimatePresence>
        {showRejectionModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowRejectionModal(false)} className="absolute inset-0 bg-background/80 backdrop-blur-xl" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-card border border-border rounded-[2.5rem] shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-black text-foreground tracking-tight">Reject Job Post</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowRejectionModal(false)} className="rounded-full w-10 h-10 border border-border">
                    <X size={20} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-bold text-muted-foreground">
                    Please provide a reason for rejecting <strong className="text-foreground">"{jobToReject?.title}"</strong>. This will be sent to the company.
                  </p>
                  <textarea
                    placeholder="e.g. Incomplete information, duplication, or poor quality..."
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    className="w-full min-h-[120px] rounded-2xl p-4 bg-secondary/20 border border-border/60 text-foreground font-medium text-sm outline-none focus:ring-2 focus:ring-rose-500/20 resize-none transition-all"
                  />
                </div>

                <div className="flex gap-4">
                  <Button onClick={() => setShowRejectionModal(false)} variant="outline" className="flex-1 rounded-2xl h-[55px] font-black uppercase tracking-widest text-xs border-border/60">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleJobApproval(jobToReject, 'rejected', rejectionReason)}
                    disabled={!rejectionReason.trim()}
                    className="flex-1 rounded-2xl h-[55px] font-black uppercase tracking-widest text-xs bg-rose-500 text-white shadow-lg shadow-rose-500/20 hover:bg-rose-600 transition-all disabled:opacity-50"
                  >
                    Confirm Rejection
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
